// apolo/src/lib/codeSchemas.js — P2-T03 (code-tab-lift).
//
// Parse, Don't Validate (Alexis King): the renderer consumes Worker responses as
// raw `res.data` and reaches deep into them (res.data.messages.map(...)). A
// malformed/changed shape then fails as `undefined is not a function` deep in a
// component, far from the boundary. These zod schemas parse the hottest Code-tab
// response shapes AT the boundary, so a bad shape surfaces THERE with a clear label.
//
// Extends apolo-api/src/lib/agentTurnSchema.js (the request half, on the Worker);
// this is the response half, on the renderer. PERMISSIVE by design (.passthrough,
// only the load-bearing fields are required) so a richer-but-valid payload never
// trips it — and the rollout is WARN-then-throw (parseBoundary defaults to 'warn':
// it logs and returns the data untouched, so live valid traffic is never broken;
// 'throw' is opted into by tests + future hardening).
import { z } from 'zod';

/** A single chat message row. Only `role` is load-bearing for rendering; the rest
 *  (content/id/created_at/tool_payload/…) passes through untouched. */
export const CodeMessageSchema = z.object({ role: z.string() }).passthrough();

/** GET /api/code/sessions/:id/messages → { messages: [...] }. */
export const MessagesResponseSchema = z
  .object({ messages: z.array(CodeMessageSchema).nullish() })
  .passthrough();

/** GET /api/code/sessions/statuses → { statuses: { [sessionId]: status } } (status
 *  derived server-side; we only assert the envelope shape, values pass through). */
export const SessionStatusesResponseSchema = z
  .object({ statuses: z.record(z.string(), z.unknown()).nullish() })
  .passthrough();

/**
 * Parse a value at a boundary. WARN-then-throw rollout:
 *  - success → returns the parsed data (passthrough keeps every field).
 *  - failure + mode 'warn' (default) → console.warn a compact "[boundary:label]
 *    path: reason" line and return the ORIGINAL value (live traffic unaffected).
 *  - failure + mode 'throw' → throw an Error with that message (the boundary teeth).
 * @template T
 * @param {{ safeParse: (v: unknown) => { success: boolean, data?: T, error?: any } }} schema
 * @param {unknown} value
 * @param {{ label?: string, mode?: 'warn'|'throw' }} [opts]
 * @returns {T}
 */
export function parseBoundary(schema, value, { label = 'boundary', mode = 'warn' } = {}) {
  const r = schema.safeParse(value);
  if (r.success) return r.data;
  const message = `[boundary:${label}] ${r.error.issues
    .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('; ')}`;
  if (mode === 'throw') throw new Error(message);
  if (typeof console !== 'undefined' && console.warn) console.warn(message);
  return /** @type {T} */ (value);
}
